import React, { useEffect, useState, useTransition } from 'react';
import { BiSearchAlt } from 'react-icons/bi';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import { getPages } from '../../actions/pages';
import { SET_PAGES_FILTERS } from '../../actions/types';
import Loader from '../../components/Loader';
import SortItem, { ISortItem } from '../../lib/SortItem/SortItem';
import { IFilters, IPage, PageTemplate } from '../../types/page';

import styles from './pagesList.module.scss';

const PagesList = () => {
  const { type } = useParams();
  const dispatch = useDispatch();
  const [isPending, startTransition] = useTransition();
  const [pagesFiltered, setPagesFiltered] = useState<IPage[]>([]);
  const { pages, list, filters } = useSelector((store: any) => store).pagesReducer;
  const { search, sort, sortDir } = filters;
  const template : PageTemplate | null = list?.templates?.find(
    (t: PageTemplate) => t.type === type,
  );

  const sortItems : ISortItem[] = [
    { value: 'title', label: template?.sort?.title || '' },
    { value: 'datePublication', label: template?.sort?.updatedAt || '' },
    { value: 'draft', label: template?.sort?.draft || '' },
  ];

  function handleChangeFilters(obj: IFilters) {
    dispatch({
      type: SET_PAGES_FILTERS,
      payload: { ...filters, ...obj },
    });
  }

  useEffect(() => {
    getPages(dispatch, { type });
  }, [type]);

  useEffect(() => {
    if (pages?.length >= 0) {
      startTransition(() => {
        let filtered = [...pages.filter(
          (p :IPage) => p?.title?.toLowerCase().includes(search.toLowerCase())
          || p?.address?.toLowerCase().includes(search.toLowerCase())
          || p?.description?.toLowerCase().includes(search.toLowerCase()),
        )];
        switch (sort) {
          case 'title':
            filtered = filtered.sort(
              (a, b) => a.content.title.localeCompare(b.content.title),
            );
            break;
          case 'draft':
            filtered = filtered.sort(
              (a, b) => b.draft - a.draft,
            );
            break;
          default:
            filtered = filtered.sort(
              (a, b) => new Date(b.datePublication).getTime()
              - new Date(a.datePublication).getTime(),
            );
            break;
        }
        if (sortDir === 'asc') {
          filtered = filtered.reverse();
        }
        setPagesFiltered(filtered);
      });
    }
  }, [pages, search, sort, sortDir]);

  return (
    <div className={styles.pagesList}>
      <header>
        <h1>{template?.label}</h1>
        <div className={styles.create}>
          <div className={styles.search}>
            <input
              value={search}
              onChange={(e) => handleChangeFilters({ search: e.target.value })}
            />
            <BiSearchAlt />
          </div>
          <NavLink className={styles.btn} to={`/pages/${type}/create`}>{template?.create}</NavLink>
        </div>
      </header>
      <div className={styles.list}>
        <div className={`${styles.row} ${styles.sort}`}>
          {sortItems.map((item) => <SortItem
            key={item.value}
            item={item}
            isAsc={!!(item.value === filters.sort && filters.sortDir === 'asc') }
            styles={styles}
            setActiveSort={(d) => handleChangeFilters({
              sort: d.value,
              sortDir: filters.sortDir === 'desc' ? 'asc' : 'desc',
            })}
          />)}
        </div>
      </div>
      {!isPending && pagesFiltered.length > 0 && (
        <ul className={styles.list}>
          {pagesFiltered.map((page: IPage) => (
            <li key={`page-${page._id}`}>
              <NavLink className={styles.row} to={`/pages/${type}/${page._id}`}>
                <div className={styles.col}>
                  {page.content.title}
                </div>
                <div className={styles.col}>
                  {page.datePublication && new Intl.DateTimeFormat('fr', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(page.datePublication))}
                </div>
                <div className={styles.col}>
                  {!page.draft && <span className={styles.success}>Publié</span>}
                </div>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
      {isPending
        && <div className={styles.loader}>
          <Loader />
        </div>
      }
    </div>
  );
};

export default PagesList;
